/**
 * Canonical 8-stage applicant lifecycle (refined spec 2026-10-11; supersedes
 * the 7-stage model for stage structure).
 *
 * Stages: draft → submitted → readyForScreening → readyForProcessing
 *          → billings → immigration → preDeparture → closed
 *
 * Transition modes:
 *  1. draft → submitted                 MANUAL (applicant or admin)
 *  2. submitted → readyForScreening     MANUAL (admin pre-approval decision)
 *  3. readyForScreening → readyForProcessing   AUTO (valid Passed screening)
 *  4. readyForProcessing → billings     AUTO (confirmation satisfied)
 *  5. billings → immigration            AUTO (one invoice cleared)
 *  6. immigration → preDeparture        AUTO (qualifying visa outcome)
 *  7. preDeparture → closed             AUTO (flight/arrival info submitted → SD)
 *  8. any open stage → closed (SC/SP)   MANUAL (reason + authority required)
 *
 * Automation NEVER performs manual transitions (§10.1). An override advances
 * at most ONE stage per override (§11).
 *
 * This module is dependency-free so it can be imported by both the client
 * bundle and the Node server.
 */

export type CanonicalStage =
  | 'draft'
  | 'submitted'
  | 'readyForScreening'
  | 'readyForProcessing'
  | 'billings'
  | 'immigration'
  | 'preDeparture'
  | 'closed';

export type TransitionMode = 'auto' | 'manual';

export const STAGE_ORDER: CanonicalStage[] = [
  'draft',
  'submitted',
  'readyForScreening',
  'readyForProcessing',
  'billings',
  'immigration',
  'preDeparture',
  'closed',
];

/** Human labels for the eight canonical stages. */
export const STAGE_LABELS: Record<CanonicalStage, string> = {
  draft: 'Draft',
  submitted: 'Submitted for Review',
  readyForScreening: 'Ready for Screening',
  readyForProcessing: 'Ready for Processing',
  billings: 'Billings',
  immigration: 'Immigration',
  preDeparture: 'Pre-Departure',
  closed: 'Closure',
};

/**
 * Transition modes keyed by "from>to". Any pair not listed here is MANUAL
 * (fail closed: automation must never guess).
 */
const TRANSITION_MODES: Record<string, TransitionMode> = {
  'draft>submitted': 'manual',
  'submitted>readyForScreening': 'manual',
  'readyForScreening>readyForProcessing': 'auto',
  'readyForProcessing>billings': 'auto',
  'billings>immigration': 'auto',
  'immigration>preDeparture': 'auto',
  'preDeparture>closed': 'auto',
  // Transition 8 (SC/SP closure from any open stage) is always manual.
};

/** Closure outcomes (spec §9). */
export type ClosureType = 'SD' | 'SC' | 'SP';

/**
 * Returns the mode for a transition. Unknown or non-adjacent pairs are
 * 'manual' (fail closed — automation never performs them).
 */
export function getTransitionMode(from: string, to: string): TransitionMode {
  return TRANSITION_MODES[`${from}>${to}`] || 'manual';
}

/** True when `to` is exactly the next stage after `from` in the lifecycle. */
export function isNextStage(from: string, to: string): boolean {
  const i = STAGE_ORDER.indexOf(from as CanonicalStage);
  return i !== -1 && STAGE_ORDER[i + 1] === to;
}

/** True when `to` is the next stage after `from` OR the same stage (gate bypass within a stage). */
export function isNextStageOrSame(from: string, to: string): boolean {
  return from === to || isNextStage(from, to);
}

/**
 * Normalises legacy stage spellings to the canonical 8-stage vocabulary.
 * Unknown values pass through unchanged (callers decide how to handle them).
 */
export function normaliseStage(raw: unknown): CanonicalStage | string {
  const s = String(raw || '').trim();
  const map: Record<string, CanonicalStage> = {
    draft: 'draft',
    Draft: 'draft',
    'application started': 'draft',
    'application_started': 'draft',
    submitted: 'submitted',
    Submitted: 'submitted',
    'submitted for review': 'submitted',
    readyforscreening: 'readyForScreening',
    ready_for_screening: 'readyForScreening',
    processing: 'readyForProcessing',
    readyforprocessing: 'readyForProcessing',
    ready_for_processing: 'readyForProcessing',
    'ready for processing': 'readyForProcessing',
    billings: 'billings',
    Billings: 'billings',
    billing: 'billings',
    'billing stage': 'billings',
    finance: 'billings',
    immigration: 'immigration',
    Immigration: 'immigration',
    pre_departure: 'preDeparture',
    predeparture: 'preDeparture',
    'pre-departure': 'preDeparture',
    'pre departure': 'preDeparture',
    closure: 'closed',
    closed: 'closed',
    Closed: 'closed',
  };
  return map[s] || map[s.toLowerCase()] || s;
}

/**
 * Stage-specific gate keys for the override dialog and the stage-incremental
 * gate panel. Each stage lists ONLY its own prerequisites — the UI never
 * shows other stages' gates (refined spec 2026-10-11).
 */
/**
 * AdminGate (owner directive, 2026-10-14): each program's phases carry
 * admin tasks (programPhaseActions); a stage's REQUIRED admin tasks are
 * themselves a matriculation gate alongside the applicant gates — the
 * `admin_tasks` prerequisite. It applies to every stage where an admin
 * works the case BEFORE the applicant may move on: submitted →
 * preDeparture. Draft is excluded (submission is applicant-driven) and
 * Closure is terminal. This single constant is the scope — compute reads
 * it, and STAGE_GATES below mirrors it so the override endpoint/dialog
 * accept `admin_tasks` as an overridable gate key at exactly these
 * stages (admin_tasks is individually overridable with a recorded
 * reason, exactly like every other gate).
 */
export const ADMIN_TASK_GATED_STAGES: CanonicalStage[] = [
  'submitted',
  'readyForScreening',
  'readyForProcessing',
  'billings',
  'immigration',
  'preDeparture',
];

/**
 * Canonical stage → program phase key: use `phaseKeyForStage` from
 * src/types/programPhases.ts (the single shared definition — the two
 * vocabularies are 1:1 except the "readyFor*" stages, whose phase keys
 * drop the prefix). Do not fork a second map here.
 */
export const STAGE_GATES: Record<string, { key: string; label: string }[]> = {
  draft: [
    { key: 'submission', label: 'Application submitted' },
  ],
  submitted: [
    { key: 'documents_approved', label: 'Documents approved' },
    { key: 'redlist_clear', label: 'Redlist check clear' },
    { key: 'contact_identity_valid', label: 'Contact & identity valid' },
    // Verification Status gate (owner directive, 2026-10-24): the Didit
    // identity verification of the user resolved by the applicant's
    // contact email must be 'approved'. A REAL gate — it blocks the
    // submitted → readyForScreening advance and is overridable per-gate
    // exactly like the others (the server validates override keys
    // against this list). Evaluated in computePreApprovalChecks (check
    // code 'verification') from snapshot.identityVerification, which
    // only the server snapshot builder populates.
    { key: 'verification_status', label: 'Verification Status' },
    { key: 'disclosure_reviewed', label: 'Criminal disclosure reviewed' },
    { key: 'program_term_valid', label: 'Program & term valid' },
    { key: 'eligibility_met', label: 'Program eligibility met' },
    { key: 'pre_approval_decision', label: 'Pre-approval decision' },
    { key: 'admin_tasks', label: 'Admin tasks complete' },
  ],
  readyForScreening: [
    { key: 'screening_passed', label: 'Passed screening record' },
    { key: 'admin_tasks', label: 'Admin tasks complete' },
  ],
  readyForProcessing: [
    { key: 'confirmation_satisfied', label: 'Placement / admission confirmation' },
    { key: 'admin_tasks', label: 'Admin tasks complete' },
  ],
  billings: [
    { key: 'invoice_cleared', label: 'Invoice cleared' },
    { key: 'admin_tasks', label: 'Admin tasks complete' },
  ],
  immigration: [
    { key: 'visa_outcome', label: 'Qualifying visa outcome' },
    { key: 'admin_tasks', label: 'Admin tasks complete' },
  ],
  preDeparture: [
    { key: 'flight_submitted', label: 'Flight / arrival information submitted' },
    { key: 'admin_tasks', label: 'Admin tasks complete' },
  ],
  closed: [],
};

/** Stage-specific matriculation button labels (clear, no generic "Move"). */
export const STAGE_ADVANCE_LABELS: Record<string, string> = {
  draft: 'Submit application',
  submitted: 'Approve & Move to Screening',
  readyForScreening: 'Advance to Processing',
  readyForProcessing: 'Advance to Billings',
  billings: 'Advance to Immigration',
  immigration: 'Advance to Pre-Departure',
  preDeparture: 'Close as Delivered',
  closed: 'Closed',
};
